// /dashboard/admin/binding-codes — issue / list / revoke 8-char invite codes
//
// The page uses /v1/invites (gated by read:user / write:user) against
// the deployed gateway. apiFetch is deliberate so cookies + CF Access
// auth flow apply.
//
// Not in sidebar — the primary「邀請使用者」 CTA lives on
// /dashboard/users and opens the same BindingCodeIssueModal inline.
// This page survives as the audit / history surface (list by status,
// revoke individual codes).

import React, { useEffect, useState, useCallback } from "react";
import {
  Plus,
  XCircle,
  RefreshCw,
} from "lucide-react";
import { apiFetch } from "../../../lib/api-client.js";
import PageHeader from "../../components/PageHeader.js";
import BindingCodeIssueModal from "../../components/BindingCodeIssueModal.js";
import { useConfirmDialog } from "../../components/ConfirmDialog.js";

type Status = "active" | "redeemed" | "revoked" | "expired";

interface BindingCode {
  code: string;
  tenant_id: string;
  role: string;
  created_by_user_id: string;
  created_by_role: string;
  note: string | null;
  created_at: number;
  expires_at: number;
  redeemed_at: number | null;
  redeemed_by_line_user_id: string | null;
  revoked_at: number | null;
  revoked_by_user_id: string | null;
  revoke_reason: string | null;
  status: Status;
}

const ROLE_LABEL: Record<string, string> = {
  tenant_admin: "管理員",
  manager: "案場主管",
  officer: "巡邏員",
  site_supervisor: "案場督導",
  incident_reporter: "異常通報員",
  auditor: "稽核員",
};

const STATUS_BADGE: Record<Status, string> = {
  active:   "badge-success",
  redeemed: "badge-default",
  revoked:  "badge-danger",
  expired:  "badge-warning",
};

function statusLabel(s: Status): string {
  switch (s) {
    case "active":   return "可使用";
    case "redeemed": return "已使用";
    case "revoked":  return "已收回";
    case "expired":  return "已過期";
    default:         return s;
  }
}
function fmt(ts: number | null): string {
  if (!ts) return "—";
  try {
    return new Date(ts * 1000).toISOString().replace("T", " ").slice(0, 16) + "Z";
  } catch { return String(ts); }
}

const TABS: { key: Status; label: string }[] = [
  { key: "active", label: "可使用" },
  { key: "redeemed", label: "已使用" },
  { key: "expired", label: "已過期" },
  { key: "revoked", label: "已收回" },
];

export default function BindingCodesPage(): React.ReactElement {
  const [tab, setTab] = useState<Status>("active");
  const [items, setItems] = useState<BindingCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      // credentials: "include" is required — api.nexus.sentry.red and
      // nexus.sentry.red are different origins, so fetch's default
      // "same-origin" omits the nexus_session cookie and we 401 every time.
      const r = await apiFetch(`/v1/invites?status=${tab}&limit=100`, { method: "GET", credentials: "include" });
      const body = await r.json().catch(() => ({})) as { ok?: boolean; results?: BindingCode[]; error?: string; correlation_id?: string };
      if (!r.ok) {
        const cid = body.correlation_id ?? r.headers.get("x-correlation-id") ?? "n/a";
        throw new Error(`HTTP ${r.status}・${body.error ?? "request_failed"}・cid=${cid}`);
      }
      setItems(body.results ?? []);
    } catch (e) {
      setErr(String((e as Error).message ?? e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { void load(); }, [load]);

  return (
    <>
      <PageHeader
        title="邀請碼"
        description="產生 8 碼邀請碼讓新成員加入。每組邀請碼僅能使用一次、可指定身分、隨時可收回。"
        actions={
          <button onClick={() => setModalOpen(true)} className="btn-primary text-sm">
            <Plus className="h-4 w-4" /> 產生邀請碼
          </button>
        }
      />

      <div className="rounded-xl border border-border bg-bg-surface p-1 inline-flex gap-1">
        {TABS.map(({ key: s, label }) => (
          <button
            key={s}
            type="button"
            onClick={() => setTab(s)}
            className={[
              "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
              tab === s ? "bg-accent/15 text-accent" : "text-text-secondary hover:bg-bg-base",
            ].join(" ")}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void load()}
          className="ml-2 px-2 py-1.5 rounded-lg text-xs text-text-secondary hover:bg-bg-base"
          aria-label="reload"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {err && (
        <div className="mt-3 rounded-md bg-danger/10 border border-danger/30 p-2 text-xs text-danger">
          目前無法載入：{err}
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-bg-base text-2xs uppercase tracking-wide text-text-muted">
            <tr>
              <th className="text-left p-3 font-medium">邀請碼</th>
              <th className="text-left p-3 font-medium">角色</th>
              <th className="text-left p-3 font-medium">建立</th>
              <th className="text-left p-3 font-medium">到期</th>
              <th className="text-left p-3 font-medium">兌換</th>
              <th className="text-left p-3 font-medium">狀態</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && !loading && (
              <tr><td colSpan={7} className="p-6 text-center text-2xs text-text-muted">目前沒有「{statusLabel(tab)}」的邀請碼</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.code} className="hover:bg-bg-base/60">
                <td className="p-3">
                  <code className="font-mono text-sm tracking-[0.2em]">{c.code}</code>
                  {c.note && <div className="text-2xs text-text-muted">{c.note}</div>}
                </td>
                <td className="p-3 text-xs">{ROLE_LABEL[c.role] ?? c.role}</td>
                <td className="p-3 text-2xs font-mono text-text-secondary">{fmt(c.created_at)}<div className="text-text-muted">by {c.created_by_user_id}</div></td>
                <td className="p-3 text-2xs font-mono text-text-secondary">{fmt(c.expires_at)}</td>
                <td className="p-3 text-2xs font-mono text-text-secondary">{fmt(c.redeemed_at)}</td>
                <td className="p-3"><span className={STATUS_BADGE[c.status]}>{statusLabel(c.status)}</span></td>
                <td className="p-3 text-right">
                  {c.status === "active" && (
                    <RevokeButton code={c.code} onDone={load} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BindingCodeIssueModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onIssued={() => void load()}
      />
    </>
  );
}

function RevokeButton({ code, onDone }: { code: string; onDone: () => void }): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const dlg = useConfirmDialog();
  async function revoke() {
    if (!(await dlg.confirm(`確認收回邀請碼 ${code}？此動作無法復原。`, { destructive: true, okLabel: "收回" }))) return;
    setBusy(true);
    try {
      const r = await apiFetch(`/v1/invites/${code}`, {
        method: "DELETE",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ reason: "revoked from dashboard" }),
        credentials: "include",
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({})) as { error?: string };
        await dlg.alert(`收回失敗：${body.error ?? r.status}`, { title: "收回失敗" });
      } else {
        onDone();
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <button type="button" onClick={revoke} disabled={busy} className="btn-ghost text-xs text-danger">
      <XCircle className="h-3.5 w-3.5" /> 撤銷
    </button>
  );
}
